import React, { Suspense } from 'react';
import { createRoot } from 'react-dom/client';

// Fontsource fonts
import '@fontsource/open-sans/300.css';
import '@fontsource/open-sans/400.css';
import '@fontsource/open-sans/600.css';
import '@fontsource/open-sans/700.css';
import '@fontsource/montserrat/400.css';
import '@fontsource/montserrat/600.css';
import '@fontsource/montserrat/700.css';
import '@fontsource/montserrat/800.css';

import { defineCustomElements } from '@ionic/pwa-elements/loader';
import { initFirebase } from './services/firebaseService';
import { setFcmServiceWorkerRegistration } from './services/pushService';
import { initPwaInstall } from './services/pwaInstallService';
// Importing useThemeStore here ensures the matchMedia subscription wires up
// at startup, before any component reads from the store.
import './store/useThemeStore';
// Side-effect import: kicks off i18next.init() so the dictionary is loading
// by the time React mounts. The <Suspense> boundary below catches the
// initial-bundle promise that useTranslation() throws on first render.
// See src/i18n/index.ts.
import './i18n';
import App from './App';
import { AppErrorBoundary } from './components/ui/AppErrorBoundary';
import { SnagScreen } from './components/ui/SnagScreen';
import { initTextScaleCap } from './services/textScaleCap';
import { initPickerGeometryFix } from './services/pickerGeometryFix';
import { initMapAuthFailureHook } from './utils/mapDiagnostics';

// Cap OS/user text scaling to the app's 150% ceiling before first paint, so
// over-scaled Android WebViews don't flash oversized text. See textScaleCap.ts.
initTextScaleCap();

// Correct the scroll geometry of Ionic's picker wheels (price, portions, and
// the availability date/time wheels). Without this they can rest past their
// last value, off the highlight pill and with no `ionChange` — see
// pickerGeometryFix.ts. Installed at boot so columns are corrected as they
// mount, before the user can reach the publish form.
initPickerGeometryFix();

// Attach the beforeinstallprompt/appinstalled listeners before React renders —
// Chrome can fire beforeinstallprompt before the app mounts, and a missed
// event means no native Android install prompt for the whole session.
initPwaInstall();

// Google calls window.gm_authFailure on key/referrer/billing rejection. Must
// exist before any map loads the JS API, so install it at boot.
initMapAuthFailureHook();

try {
  defineCustomElements(window);
} catch {
  /* Ionic PWA elements registration is non-fatal */
}

try {
  initFirebase();
} catch {
  /* Firebase init failure is non-fatal at boot — features that need it will
     surface their own errors later, and the ErrorBoundary catches render-time
     fallout. Better than letting one bad SDK call kill the whole bundle. */
}

// Register the FCM service worker under a narrow scope so a future PWA SW
// (offline shell, app-update workflow with skip-waiting, etc.) can register
// at root scope `/` without colliding. FCM only needs the SW to be reachable
// — it doesn't care what scope path we pick, because the scope is a virtual
// key, not a routing constraint. Once registered we stash the handle so
// pushService can pass it explicitly to FirebaseMessaging.getToken() rather
// than letting the SDK auto-discover at `/`.
if ('serviceWorker' in navigator) {
  navigator.serviceWorker
    .register('/firebase-messaging-sw.js', {
      scope: '/firebase-cloud-messaging-push-scope',
    })
    .then((reg) => setFcmServiceWorkerRegistration(reg))
    .catch(() => {
      /* SW registration failure is non-fatal — push silently no-ops, app keeps working */
    });
}

// The legacy one-shot reload-at-root cache buster has been removed. Its job
// was to force a fresh index.html after deploys so users on stale tabs
// pulled new chunk hashes. The PWA SW (registered via vite-plugin-pwa's
// useRegisterSW hook inside <PwaUpdateInitializer />) now handles that
// properly: new SW installs in the background, banner appears, user clicks
// to apply, page reloads with new shell + chunks. The /ping minimum-version
// lifeline (versionCheckService) covers hard-cutoff cases on top of that.
// Leaving the old hack in would compete with the SW update flow.

const container = document.getElementById('root');
const root = createRoot(container!);

try {
  root.render(
    <React.StrictMode>
      <AppErrorBoundary>
        {/* Suspense covers the i18n http-backend's first-bundle load.
            Fallback is null because the index.html splash + PWA service
            worker already paint the boot frame — a separate spinner here
            would just flash on top of them. Per-screen lazy bundles get
            their own inner Suspense in App.tsx. */}
        <Suspense fallback={null}>
          <App />
        </Suspense>
      </AppErrorBoundary>
    </React.StrictMode>
  );
} catch {
  // If the very first synchronous render throws (something exploded before
  // the ErrorBoundary could mount), fall back to the bare SnagScreen so the
  // user gets a reload affordance instead of a blank page that Safari will
  // eventually condemn as "A problem repeatedly occurred."
  root.render(<SnagScreen />);
}
